import {
	DataProtectionOfficerData,
	PrivacyPolicyOrganisationInformationData,
	usePrivacyPolicyShow,
} from '@jobilla/nexus-sdk'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Jobibot, SubtleLoader } from '../../../ui'

export const Route = createFileRoute('/company/$companyId/privacy-policy')({
	component: RouteComponent,
})

function RouteComponent() {
	const { companyId } = Route.useParams()
	const { t } = useTranslation('common')

	const privacyPolicyQuery = usePrivacyPolicyShow(companyId)

	const navigate = useNavigate()
	const goBack = () =>
		navigate({
			to: '/auth',
		})

	return (
		<div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
			<article className="mx-auto max-w-3xl mb-2 flex justify-between flex-row items-center">
				<div onClick={goBack} className="cursor-pointer text-blue-600 hover:underline mb-4">
					<Jobibot size={50} />
				</div>
			</article>
			<article className="mx-auto max-w-3xl bg-white shadow-sm rounded-lg p-8 sm:p-12">
				<header className="mb-10">
					<h1 className="text-3xl font-bold text-gray-900 mb-2">
						{t('privacyPolicy.pageTitle')}
					</h1>
				</header>

				<SummaryBox />

				<Section title={t('privacyPolicy.introductionTitle')}>
					<p>
						{t('privacyPolicy.introductionText')}
					</p>
				</Section>

				{privacyPolicyQuery?.isSuccess && privacyPolicyQuery.data?.organisationInformation
					? <DataControllerSection company={privacyPolicyQuery.data?.organisationInformation} />
					: <SubtleLoader delay={0} />}

				{privacyPolicyQuery.isSuccess && privacyPolicyQuery.data?.dataProtectionOfficer
					? <DataProtectionOfficerSection dpo={privacyPolicyQuery.data.dataProtectionOfficer} />
					: null}

				<DataProcessorSection />

				<Section title={t('privacyPolicy.informationTitle')}>
					<p>
						{t('privacyPolicy.informationIntro')}
					</p>
					<ul className="list-disc pl-6 mt-3 space-y-2">
						<li>{t('privacyPolicy.informationName')}</li>
						<li>{t('privacyPolicy.informationProcess')}</li>
						<li>{t('privacyPolicy.informationFiles')}</li>
						<li>{t('privacyPolicy.informationLinks')}</li>
					</ul>
				</Section>

				<Section title={t('privacyPolicy.purposeTitle')}>
					<p>{t('privacyPolicy.purposeIntro')}</p>
					<ul className="list-disc pl-6 mt-3 space-y-2">
						<li>{t('privacyPolicy.purposeManage')}</li>
						<li>{t('privacyPolicy.purposeCommunicate')}</li>
						<li>{t('privacyPolicy.purposeSupport')}</li>
					</ul>
				</Section>

				<Section title={t('privacyPolicy.legalBasisTitle')}>
					<p>{t('privacyPolicy.legalBasisIntro')}</p>
					<ul className="list-disc pl-6 mt-3 space-y-2">
						<li>{t('privacyPolicy.legalBasisConsent')}</li>
						<li>{t('privacyPolicy.legalBasisContract')}</li>
					</ul>
				</Section>

				<Section title={t('privacyPolicy.dataSharingTitle')}>
					<p>{t('privacyPolicy.dataSharingIntro')}</p>
					<ul className="list-disc pl-6 mt-3 space-y-2">
						<li>{t('privacyPolicy.dataSharingJobilla')}</li>
						<li>{t('privacyPolicy.dataSharingKombo')}</li>
					</ul>
					<p className="mt-3">
						{t('privacyPolicy.dataSharingNote')}
					</p>
				</Section>

				<Section title={t('privacyPolicy.dataRetentionTitle')}>
					<p>
						{t('privacyPolicy.dataRetentionText')}
					</p>
				</Section>

				<Section title={t('privacyPolicy.dataSecurityTitle')}>
					<p>
						{t('privacyPolicy.dataSecurityText')}
					</p>
				</Section>

				<Section title={t('privacyPolicy.yourRightsTitle')}>
					<p>{t('privacyPolicy.yourRightsIntro')}</p>
					<ul className="list-disc pl-6 mt-3 space-y-2">
						<li>
							<strong>{t('privacyPolicy.rightAccessLabel')}</strong> –{' '}
							{t('privacyPolicy.rightAccessText')}
						</li>
						<li>
							<strong>{t('privacyPolicy.rightCorrectionLabel')}</strong> –{' '}
							{t('privacyPolicy.rightCorrectionText')}
						</li>
						<li>
							<strong>{t('privacyPolicy.rightWithdrawLabel')}</strong> –{' '}
							{t('privacyPolicy.rightWithdrawText')}
						</li>
						<li>
							<strong>{t('privacyPolicy.rightObjectLabel')}</strong> –{' '}
							{t('privacyPolicy.rightObjectText')}
						</li>
						<li>
							<strong>{t('privacyPolicy.rightPortabilityLabel')}</strong> –{' '}
							{t('privacyPolicy.rightPortabilityText')}
						</li>
					</ul>
				</Section>

				<Section title={t('privacyPolicy.exerciseRightsTitle')}>
					<p>
						{t('privacyPolicy.exerciseRightsText')}
					</p>
					<p className="mt-3">
						{t('privacyPolicy.exerciseRightsComplaint')}
					</p>
				</Section>

				<Section title={t('privacyPolicy.policyChangesTitle')}>
					<p>
						{t('privacyPolicy.policyChangesText')}
					</p>
				</Section>
			</article>
		</div>
	)
}

function SummaryBox() {
	const { t } = useTranslation('common')

	return (
		<div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-10">
			<p className="text-blue-900 font-medium">
				<strong>{t('privacyPolicy.summaryLabel')}</strong> {t('privacyPolicy.summaryText')}
			</p>
		</div>
	)
}

interface SectionProps {
	title: string
	children: React.ReactNode
}

function Section({ title, children }: SectionProps) {
	return (
		<section className="mb-8">
			<h2 className="text-xl font-semibold text-gray-900 mb-4">{title}</h2>
			<div className="text-gray-700 leading-relaxed">{children}</div>
		</section>
	)
}

interface DataControllerSectionProps {
	company: PrivacyPolicyOrganisationInformationData
}

function DataControllerSection({ company }: DataControllerSectionProps) {
	const { t } = useTranslation('common')

	const addressLines = [
		company?.streetAddress,
		[company?.postalCode, company?.city].filter(Boolean).join(' ') || null,
		company?.country,
	].filter((line): line is string => Boolean(line))

	const contactLines = [
		company?.phoneNumber,
		company?.emailAddress,
	].filter((line): line is string => Boolean(line))

	return (
		<Section title={t('privacyPolicy.dataControllerTitle')}>
			<p className="mb-4">{t('privacyPolicy.dataControllerIntro')}</p>
			<address className="not-italic bg-gray-50 rounded-lg p-4 space-y-1">
				<p className="font-semibold">{company?.companyName}</p>
				{addressLines.map((line, index) => <p key={index}>{line}</p>)}
				{contactLines.map((line, index) => <p key={index}>{line}</p>)}
			</address>
			<p className="mt-4 text-gray-600 italic">
				{t('privacyPolicy.dataControllerPronouns')}
			</p>
			<p className="mt-4">
				{t('privacyPolicy.dataControllerContact')}
			</p>
		</Section>
	)
}

interface DataProtectionOfficerSectionProps {
	dpo: DataProtectionOfficerData
}

function DataProtectionOfficerSection({ dpo }: DataProtectionOfficerSectionProps) {
	const { t } = useTranslation('common')

	if (!dpo.name && !dpo.emailAddress) {
		return null
	}

	return (
		<Section title={t('privacyPolicy.dpoTitle')}>
			<address className="not-italic bg-gray-50 rounded-lg p-4 space-y-1">
				<p>
					{t('privacyPolicy.dpoName')} {dpo.name ? <span className="font-semibold">{dpo.name}</span> : null}
				</p>
				<p>{t('privacyPolicy.dpoPhone')} {dpo.phoneNumber ? <span>{dpo.phoneNumber}</span> : null}</p>
				<p>{t('privacyPolicy.dpoEmail')} {dpo.emailAddress ? <span>{dpo.emailAddress}</span> : null}</p>
			</address>
		</Section>
	)
}

function DataProcessorSection() {
	const { t } = useTranslation('common')

	return (
		<Section title={t('privacyPolicy.dataProcessorTitle')}>
			<p className="mb-4">
				{t('privacyPolicy.dataProcessorIntro')}
			</p>
			<address className="not-italic bg-gray-50 rounded-lg p-4 space-y-1">
				<p className="font-semibold">{t('privacyPolicy.dataProcessorName')}</p>
				<p>{t('privacyPolicy.dataProcessorAddress')}</p>
				<p>{t('privacyPolicy.dataProcessorBusinessId')}</p>
			</address>
			<p className="mt-4">
				{t('privacyPolicy.dataProcessorNote')}
			</p>
		</Section>
	)
}
